import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import RichTextEditor from "@/components/admin/RichTextEditor";

type Row = {
  key: string;
  value: string;
  type: "text" | "richtext";
  group_name: string;
  label: string;
  sort_order: number;
};

const groupOrder = ["hero", "sections", "nav", "footer"];
const groupLabels: Record<string, string> = {
  hero: "Hero section",
  sections: "Section headings & buttons",
  nav: "Navigation",
  footer: "Footer",
};

const AdminContent = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("site_content")
      .select("*")
      .order("group_name")
      .order("sort_order");
    if (error) toast.error(error.message);
    const list = (data as Row[]) ?? [];
    setRows(list);
    setDraft(Object.fromEntries(list.map((r) => [r.key, r.value])));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (key: string) => {
    setSavingKey(key);
    const { error } = await supabase
      .from("site_content")
      .update({ value: draft[key] ?? "" })
      .eq("key", key);
    setSavingKey(null);
    if (error) return toast.error(error.message);
    toast.success("Saved");
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, value: draft[key] } : r)));
  };

  const grouped: Record<string, Row[]> = {};
  for (const r of rows) {
    grouped[r.group_name] = grouped[r.group_name] ?? [];
    grouped[r.group_name].push(r);
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="font-display text-3xl">Site content</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Edit homepage copy. Changes go live as soon as you save each field.
        </p>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-8">
          {groupOrder
            .filter((g) => grouped[g])
            .map((g) => (
              <section key={g} className="bg-card rounded-[1.5rem] p-6 shadow-card">
                <h2 className="font-display text-xl mb-4">{groupLabels[g] ?? g}</h2>
                <div className="space-y-5">
                  {grouped[g].map((r) => {
                    const dirty = (draft[r.key] ?? "") !== r.value;
                    return (
                      <div key={r.key}>
                        <div className="flex items-baseline justify-between gap-3 mb-1.5">
                          <label className="text-xs font-semibold uppercase tracking-wider">
                            {r.label || r.key}
                          </label>
                          <span className="text-[10px] text-muted-foreground font-mono">{r.key}</span>
                        </div>
                        {r.type === "richtext" ? (
                          <RichTextEditor
                            value={draft[r.key] ?? ""}
                            onChange={(v) => setDraft((d) => ({ ...d, [r.key]: v }))}
                          />
                        ) : r.value.length > 60 || r.key.includes("intro") || r.key.includes("subheading") ? (
                          <textarea
                            rows={3}
                            value={draft[r.key] ?? ""}
                            onChange={(e) => setDraft((d) => ({ ...d, [r.key]: e.target.value }))}
                            className="w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                          />
                        ) : (
                          <input
                            value={draft[r.key] ?? ""}
                            onChange={(e) => setDraft((d) => ({ ...d, [r.key]: e.target.value }))}
                            className="w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                          />
                        )}
                        <div className="flex justify-end gap-2 mt-2">
                          {dirty && (
                            <button
                              onClick={() => setDraft((d) => ({ ...d, [r.key]: r.value }))}
                              className="px-3 py-1.5 rounded-full bg-muted text-xs font-semibold"
                            >
                              Reset
                            </button>
                          )}
                          <button
                            onClick={() => save(r.key)}
                            disabled={!dirty || savingKey === r.key}
                            className="px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-secondary transition-colors disabled:opacity-40"
                          >
                            {savingKey === r.key ? "Saving…" : dirty ? "Save" : "Saved"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminContent;
